import {
    API_AUTH_HEADER,
    API_AUTH_TOKEN_PREFIX, APP_SUSPENDED_URL,
    HTTP_STATUS_CODES,
    REQUIRED_USER_ACTIONS,
    CAPTCHA_CHALLENGES, TYPES_2FA_OTP, TWO_FA_TYPES_MAPPER, TWO_FA_CONTEXT_FLOW,
} from '../common/const'
import AuthService from '../index'
import openModal from '../requiredUserAction'
import ConfirmationChallenge from "@emcd-shared/captcha-service/ui"
import AnalyticsService from "@emcd-shared/analytics-service";
import StreamService from '../services/stream';
import {ConfirmationPasskey, Confirmation2FA} from '../requiredUserAction/actions'

export function requestInterceptor(config) {
    const res = {...config}
    const token = AuthService.getAccessToken()
    if (
        !['/auth/login', '/auth/refresh', '/statsv2?emcd=1'].includes(config.url) &&
        !config?.headers?.[API_AUTH_HEADER] // 'X-Access-Token'
    ) {
        if (typeof res.headers === 'object') {
            // если в config был передан headers: {}
            AnalyticsService.addBreadcrumb('AuthService.requestInterceptor', {
                description: 'Set access token to api',
                api: config?.url,
                access_token: token?.slice(-5),
            });
            res.headers[API_AUTH_HEADER] = API_AUTH_TOKEN_PREFIX + token;
        } else {
            AnalyticsService.addBreadcrumb(
                'AuthService.requestInterceptor',
                'Token was not passed to the request headers'
            );
        }
    }

    const isRefreshRequest = config.url.includes('/auth/refresh');
    const isStreamOpen = StreamService.isOpen();

    if (isStreamOpen) {
        AnalyticsService.addBreadcrumb('AuthService.requestInterceptor', {
            description: "Stream is already open",
            api: config.url,
            access_token: token?.slice(-5),
        })
        if (!isRefreshRequest) {
            return StreamService.promise.then((response) => {
                AnalyticsService.addBreadcrumb('AuthService.requestInterceptor', {
                    description: "Refresh is resolve, set in config new token",
                    api: config.url,
                    access_token: token?.slice(-5),
                })
                res.headers[API_AUTH_HEADER] = API_AUTH_TOKEN_PREFIX + response?.access_token;
                return res;
            });
        }
    } else {
        if (isRefreshRequest) {
            AnalyticsService.addBreadcrumb('AuthService.requestInterceptor', {
                description: "Stream is not open, call open stream",
                api: config.url,
                access_token: token?.slice(-5),
            })
            StreamService.open();
        }
        AnalyticsService.addBreadcrumb('AuthService.requestInterceptor', {
            description: "Return config",
            api: config.url,
            access_token: token?.slice(-5),
        })
        return res;
    }
}

export function requestErrorInterceptor(error) {
    return Promise.reject(error)
}

export function responseInterceptor(response) {
    return response
}

export function responseErrorInterceptor(axiosInstance, saveDataCallBack, instance) {
    return function (error) {
        const token = AuthService.getAccessToken()
        AnalyticsService.addBreadcrumb('AuthService.responseErrorInterceptor', 'responseErrorInterceptor')
        AnalyticsService.addBreadcrumb('AuthService.responseErrorInterceptor', {
            description: "Get error from api",
            api: error?.config?.url,
            access_token: token?.slice(-5),
            code: error.response?.data?.code ?? error.code,
            message: error.response?.data?.message ?? JSON.stringify(error.response?.data),
        })
        const newErr = {...error}

        // TODO(i.zhuravlev) hotfix for 444 correct error handling, make correct error handling
        if (!error?.response) {
            if (error?.request?.status) {
                newErr.response = {
                    ...error.response,
                    status: error.request.status,
                    data: error.request.response
                }
            }
        }

        const originalConfig = error.config
        // Skip auth flow for structured business errors (e.g. 403 kyb-tier-too-low) — they are not auth failures
        const isBusinessError = !!newErr?.response?.data?.error?.code
        const isUnauthorized =
            newErr.response && // have response
            (newErr?.response?.status === HTTP_STATUS_CODES.UNAUTHORIZED || newErr?.response?.status === HTTP_STATUS_CODES.FORBIDDEN) && // 401 || 403
            !isBusinessError && // not a structured business error
            !originalConfig._retry && // once
            originalConfig.url !== '/auth/login' // not login action

        const isRequiredUserAction =
            newErr.response && // have response
            newErr?.response?.status === HTTP_STATUS_CODES.REQUIRED_USER_ACTION && // 444
            !originalConfig._retry // once

        const isStreamOpen = StreamService.isOpen();

        if (isUnauthorized) {
            AnalyticsService.addBreadcrumb('AuthService.responseErrorInterceptor', 'isUnauthorized')
            originalConfig._retry = true
            // Refresh token is expired
            const isPossibleAuthorize = AuthService.isPossibleAuthorize()
            if (!isPossibleAuthorize) {
                AnalyticsService.warning('AuthService.responseErrorInterceptor.isPossibleAuthorize')
                AuthService.logout()
                return Promise.reject('Unauthorised')
            } else if (isStreamOpen) {
                AnalyticsService.addBreadcrumb('AuthService.responseErrorInterceptor', {
                    description: "Get 401 and stream is open, await promise stream",
                    api: error?.config?.url,
                    access_token: token?.slice(-5),
                })
                return StreamService.promise
                    .then((res) => {
                        originalConfig.headers[API_AUTH_HEADER] = API_AUTH_TOKEN_PREFIX + res?.access_token;
                        AnalyticsService.addBreadcrumb('AuthService.responseErrorInterceptor', {
                            description: "Stream is resolve, make call with new token",
                            api: error?.config?.url,
                            access_token: token?.slice(-5),
                        })
                        return axiosInstance(originalConfig);
                    })
                    .catch((err) => {
                        AnalyticsService.addBreadcrumb('AuthService.responseErrorInterceptor', {
                            description: "Get error from StreamService.promise",
                            message: JSON.stringify(err),
                            api: err?.config?.url,
                            access_token: token?.slice(-5),
                        })
                        return Promise.reject(err)
                    });
            } else {
                // * Refresh token is expired
                return AuthService.refresh(axiosInstance, saveDataCallBack)
                    .then((res) => {
                        AnalyticsService.addBreadcrumb('AuthService.responseErrorInterceptor', 'refresh.response')
                        if (res?.access_token) {
                            originalConfig.headers[API_AUTH_HEADER] = API_AUTH_TOKEN_PREFIX + res.access_token
                            AnalyticsService.addBreadcrumb('AuthService.responseErrorInterceptor', {
                                description: "Get resolve from AuthService.refresh, make recall with new token",
                                access_token: token?.slice(-5),
                            })
                            return axiosInstance(originalConfig)
                        } else {
                            AnalyticsService.error('Refresh Bad response')
                            AnalyticsService.log(res)
                            return Promise.reject('Unauthorised')
                        }
                    })
                    .catch(err => {
                        AnalyticsService.addBreadcrumb('AuthService.responseErrorInterceptor', 'refresh.response.error')
                        if (err.response) {
                            AnalyticsService.log(err.response.status)
                            AnalyticsService.log(JSON.stringify(err.response.data))
                            AnalyticsService.log(JSON.stringify(err.response.headers))
                        }
                        if (err.request) {
                            AnalyticsService.log(err.request.status)
                            AnalyticsService.log(JSON.stringify(err.request.data))
                            AnalyticsService.log(JSON.stringify(err.request.headers))
                        }
                        AnalyticsService.error(err)
                        AuthService.logout()
                        return Promise.reject(err)
                    })
            }
        }

        if (isRequiredUserAction) {
            AnalyticsService.addBreadcrumb('AuthService.responseErrorInterceptor', 'isRequiredUserAction')
            if (!newErr?.response?.data?.required || !newErr?.response?.data?.secret_key) {
                AnalyticsService.addBreadcrumb('AuthService.responseErrorInterceptor', 'isRequiredUserAction.error')
                if (newErr.response) {
                    AnalyticsService.log(newErr.response.status)
                    AnalyticsService.log(JSON.stringify(newErr.response.data))
                    AnalyticsService.log(JSON.stringify(newErr.response.headers))
                }
                AnalyticsService.error('Incorrect response structure')
                return Promise.reject('Incorrect response structure')
            }
            const [requiredAction] = newErr?.response?.data?.required || []
            const requiredPasskeyAction = newErr?.response?.data?.required.includes(REQUIRED_USER_ACTIONS.PASSKEY)
            const API_URL = error?.config?.url
            const FLOW_MAP = {
                '/oauth/login/phone': 'registration',
                '/oauth/register/phone': 'auth',
            };
            const FLOW = newErr?.response?.data.flow || ''

            AnalyticsService.setContextVariable('requiredAction', requiredAction)
            if (requiredPasskeyAction) {
                return openModal(
                    ConfirmationPasskey,
                    {
                        secretKey: newErr?.response?.data?.secret_key,
                        passkeyData: newErr?.response?.data?.passkey,
                        axios: axiosInstance,
                        request: originalConfig,
                    },
                    instance)
                    .then((res) => {
                        return res
                    })
                    .catch((error) => {
                        if (error === 'CHANGE_TYPE') {
                            const requiredActionExceptPasskey = newErr?.response?.data?.required.find(
                                (action) => action !== REQUIRED_USER_ACTIONS.PASSKEY
                            )

                            return openModal(
                                Confirmation2FA,
                                {
                                    type: TWO_FA_TYPES_MAPPER[requiredActionExceptPasskey] || TYPES_2FA_OTP.SMS,
                                    'secret-key': newErr?.response?.data?.secret_key,
                                    nextMethod: newErr?.response?.data?.next_two_fa_method_type || "SMS",
                                    inputLength: newErr?.response?.data?.current_two_fa_code_len || 4,
                                    resendOnOpen: true,
                                },
                                instance
                            )
                                .then((res) => {
                                    if (!res) {
                                        AnalyticsService.error('Bad FunctionBasedComponent resolve')
                                    }
                                    originalConfig.headers['secret-key'] = newErr?.response?.data?.secret_key
                                    originalConfig.headers['secret-value'] = res
                                    originalConfig.headers["2fa-method"] = requiredActionExceptPasskey;
                                    // retry last request
                                    return axiosInstance(originalConfig)
                                })
                                .catch((e) => {
                                    AnalyticsService.error(e)
                                    return Promise.reject(e)
                                })

                        }
                    })
            } else if (FLOW_MAP[API_URL] === FLOW) {
                return Promise.reject(error)
            } else if (requiredAction === REQUIRED_USER_ACTIONS.TWO_FA_GOOGLE) {
                return openModal(
                    Confirmation2FA,
                    {
                        type: TYPES_2FA_OTP.GOOGLE,
                        'secret-key': newErr?.response?.data?.secret_key,
                        nextMethod: TYPES_2FA_OTP.GOOGLE,
                        inputLength: newErr?.response?.data?.current_two_fa_code_len || 6,
                    },
                    instance
                )
                    .then((res) => {
                        if (!res) {
                            AnalyticsService.error('Bad FunctionBasedComponent resolve')
                        }
                        originalConfig.headers['secret-key'] = newErr?.response?.data?.secret_key
                        originalConfig.headers['secret-value'] = res
                        // retry last request
                        return axiosInstance(originalConfig).catch((retryErr) => {
                            const $t = instance?._context?.config?.globalProperties?.$t
                            const $toast = instance?._context?.config?.globalProperties?.$toast
                            $toast?.({type: 'error', title: $t?.('2faModal | invalidCode') || 'Wrong code'})
                            return Promise.reject(retryErr)
                        })
                    })
                    .catch((e) => {
                        AnalyticsService.error(e)
                        return Promise.reject(e)
                    })
            } else if (requiredAction === REQUIRED_USER_ACTIONS.TWO_FA_SMS) {
                const flow = newErr?.response?.data?.flow || TWO_FA_CONTEXT_FLOW.DEFAULT;
                const HEADER_PREFIX = {
                    [TWO_FA_CONTEXT_FLOW.REGISTRATION]: '2fa-',
                    [TWO_FA_CONTEXT_FLOW.AUTH]: '2fa-',
                    [TWO_FA_CONTEXT_FLOW.DEFAULT]: '',
                };
                const prefix = HEADER_PREFIX[flow || 'default'];

                return openModal(
                    Confirmation2FA,
                    {
                        type: newErr?.response?.data?.current_two_fa_method_type || "GENERAL",
                        'secret-key': newErr?.response?.data?.secret_key,
                        nextMethod: newErr?.response?.data?.next_two_fa_method_type || "SMS",
                        inputLength: newErr?.response?.data?.current_two_fa_code_len || 4,
                        flow: flow,
                        onValidate: async (code) => {
                            originalConfig.headers[`${prefix}secret-key`] = newErr?.response?.data?.secret_key;
                            originalConfig.headers[`${prefix}secret-value`] = code;
                            return axiosInstance(originalConfig);
                        },
                    },
                    instance
                )
                    .catch((e) => {
                        AnalyticsService.error(e)
                        return Promise.reject(e)
                    })
            } else if (requiredAction === REQUIRED_USER_ACTIONS.SUSPENDED) {
                AnalyticsService.addBreadcrumb('AuthService', 'responseErrorInterceptor.suspended')
                AnalyticsService.setContextVariable('fromUrl', window.location.href)
                const suspendedUrl = new URL(APP_SUSPENDED_URL)
                window.location.replace(suspendedUrl.toString())
                return Promise.resolve()
            } else if (requiredAction === REQUIRED_USER_ACTIONS.REGISTRATION) {
                AnalyticsService.addBreadcrumb('AuthService', 'responseErrorInterceptor.registration')
                originalConfig.headers['secret-key'] = newErr?.response?.data?.secret_key
                originalConfig.headers['secret-value'] = ''
                try {
                    const requestData = JSON.parse(originalConfig.data)
                    requestData.promocode = ''
                    originalConfig.data = JSON.stringify(requestData)
                } catch (e) {
                    AnalyticsService.warning('responseErrorInterceptor.registration: non-JSON body, retrying as-is')
                }
                return axiosInstance(originalConfig)
            } else if (CAPTCHA_CHALLENGES.includes(requiredAction)) {
                return openModal(
                    ConfirmationChallenge,
                    {
                        challenge: requiredAction,
                        challengeData: newErr?.response?.data?.task,
                        context: {
                            i18n: {
                                t: instance?._context?.config?.globalProperties?.$t
                            }
                        }
                    },
                    instance)
                    .then((token) => {
                        originalConfig.headers['secret-key'] = newErr?.response?.data?.secret_key
                        originalConfig.headers['captcha-token'] = token
                        return axiosInstance(originalConfig)
                    })
            } else {
                const errorMessage = `Unsupported user action: ${requiredAction}`
                AnalyticsService.error(errorMessage)
                return Promise.reject(errorMessage)
            }
        }

        return Promise.reject(error)
    }
}

export default {
    requestInterceptor,
    requestErrorInterceptor,
    responseInterceptor,
    responseErrorInterceptor,
}
